/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { css } from "@emotion/css";
import { ExternalLink, StatusBadge } from "@octopusdeploy/design-system-components";
import { borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { Repository, MachineModelHealthStatusValues, isWorkerMachine } from "@octopusdeploy/octopus-server-client";
import type { TenantResource, MachineResource, WorkerPoolSummaryResource, ResourceCollection, WorkerMachineResource, PaginatedCollection, MachineModelHealthStatus, EnvironmentSummaryV2Resource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type { Dictionary } from "lodash";
import type { ReactNode } from "react";
import { orderedHealthStatuses } from "~/areas/infrastructure/InfrastructureDetails";
import { DisabledMachineIcon } from "~/areas/infrastructure/components/MachineHealthStatusIcons/DisabledMachineIcon";
import { MachineHealthStatusIcon } from "~/areas/infrastructure/components/MachineHealthStatusIcons/MachineHealthStatusIcon";
import { machineHealthStatusToStatusBadgeStatus } from "~/areas/infrastructure/components/MachineHealthStatusIcons/machineHealthStatusToStatusBadgeStatus";
import type { EndpointRegistration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import type { DataBaseComponentState } from "~/components/DataBaseComponent/DataBaseComponent";
import { DataBaseComponent } from "~/components/DataBaseComponent/DataBaseComponent";
import List from "~/components/PagingList";
import { Section } from "~/components/Section/Section";
import { FormSectionHeading } from "~/components/form";
import type { TagIndex } from "~/components/tagSets";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper";
import MachineRow from "../MachineRow/MachineRow";
import { createMachineHealthMap, toPaginatedCollection } from "../MachinesLayout/MachineFilter";
import styles from "./style.module.less";
interface SmallCloseButtonProps {
    onClose: (e: React.MouseEvent<Element, MouseEvent>) => void;
}
class MachinesList extends List<MachineResource> {
}
export interface BaseMachinesSummaryProps {
    isFiltering: boolean;
    tenants: TenantResource[];
    tagIndex: TagIndex;
    isNewStatusIconsFeatureEnabled?: boolean;
}
// TODO: Deal with the state where we have no MachinesResponse - do we even do anything here?
export interface BaseMachinesSummaryState extends DataBaseComponentState {
    machinesResponse: PaginatedCollection<MachineResource> | null;
    machineHealthStatusFastLookup: Dictionary<PaginatedCollection<MachineResource>>;
    currentPageIndex: number; // This has a custom endpoint, so we manage our own paging implementation in List/onLoadMore.
    expanded: boolean; // Need to know if we're currently expanded so we can choose to reload when the filter changes or not.
    healthStatusFilter: string;
    isDisabledFilter: boolean | undefined;
    redirectToTaskId?: string;
    redirectToTasks: boolean;
    endpointRegistrations: EndpointRegistration[];
}
abstract class BaseMachinesSummary<Props extends BaseMachinesSummaryProps, State extends BaseMachinesSummaryState> extends DataBaseComponent<Props, State> {
    protected machineListTakeSize = Repository.takeDefaultPageSize;
    protected abstract loadData(): Promise<Pick<State, keyof State> | void>;
    protected reloadDataAndCurrentPageIndex() {
        this.setState({ currentPageIndex: 0 }, async () => {
            await this.doBusyTask(async () => {
                await this.loadData();
            });
        });
    }
    protected renderMachinesList(summary: EnvironmentSummaryV2Resource | WorkerPoolSummaryResource) {
        const machinesResponse: PaginatedCollection<MachineResource> | null = this.state.machinesResponse;
        return <div key={"allMachines"}>{this.state.expanded && machinesResponse && <div>{this.renderMachinesListGroupedByHealthStatus(summary, machinesResponse)}</div>}</div>;
    }
    protected assembleHealthStatusFastLookup(machinesResponse: ResourceCollection<WorkerMachineResource | MachineResource>) {
        return createMachineHealthMap(machinesResponse, this.machineListTakeSize, this.state.machineHealthStatusFastLookup);
    }
    protected setMachineResponseState(machinesResponse: ResourceCollection<WorkerMachineResource | MachineResource>) {
        const machineHealthStatusFastLookup = this.assembleHealthStatusFastLookup(machinesResponse);
        this.setState({ machinesResponse, machineHealthStatusFastLookup });
    }
    private renderMachine = (machine: MachineResource, needsUpgrading: boolean = false): ReactNode => {
        return <MachineRow registrations={this.state.endpointRegistrations} machine={machine} tenants={this.props.tenants} tagIndex={this.props.tagIndex} needsUpgrading={needsUpgrading}/>;
    };
    private renderMachinesListGroupedByHealthStatus(summary: EnvironmentSummaryV2Resource | WorkerPoolSummaryResource, machinesResponse: PaginatedCollection<MachineResource>) {
        const allMachines = machinesResponse.Items;
        if (allMachines.length === 0) {
            return <Section>{isEnvironmentSummary(summary) ? <EmptyStateNoMachine title={"There are currently no machines in this environment."}/> : <EmptyStateNoMachine title={"There are currently no workers in this worker pool."}/>}</Section>;
        }
        let machinesNeedUpgrading: string[] = [];
        if (summary.MachineIdsForCalamariUpgrade) {
            machinesNeedUpgrading = machinesNeedUpgrading.concat(summary.MachineIdsForCalamariUpgrade);
        }
        if (summary.MachineIdsForTentacleUpgrade) {
            machinesNeedUpgrading = machinesNeedUpgrading.concat(summary.MachineIdsForTentacleUpgrade);
        }
        return (<div>
                {orderedHealthStatuses.map((status) => this.renderHealthStatusSectionHeading(status, allMachines, machinesNeedUpgrading))}
                {this.renderDisabledSectionHeading(allMachines)}
            </div>);
    }
    private renderHealthStatusSectionTitle(status: MachineModelHealthStatus, machines: MachineResource[], allMachines: MachineResource[]) {
        const title = (<div className={styles.healthStatusCardTitleContainer} key={status}>
                <div className={styles.healthStatusIconContainer}>
                    {this.props.isNewStatusIconsFeatureEnabled ? <StatusBadge status={machineHealthStatusToStatusBadgeStatus(status)} size="small"/> : <MachineHealthStatusIcon healthStatus={status} title="Health status"/>}
                </div>
                <div className={styles.healthStatusName}>{MachineHealthStatusHelper.getFriendlyName(status)}</div>
                <div className={styles.healthStatusMachinesCount}>{`(${machines.length}/${allMachines.length})`}</div>
            </div>);
        return <FormSectionHeading title={title}/>;
    }
    private renderHealthStatusSectionHeading(status: MachineModelHealthStatus, allMachines: MachineResource[], machineIdsTobeUpgraded: string[]): JSX.Element | null {
        let machines = allMachines.filter((x) => x.HealthStatus === status);
        if (status === MachineModelHealthStatusValues.Unknown) {
            machines = machines.filter((x) => !x.IsDisabled);
        }
        const componentKey = status.toString();
        return machines.length > 0 ? (<div key={status.toString()}>
                {this.renderHealthStatusSectionTitle(status, machines, allMachines)}
                <MachinesList key={componentKey} initialData={this.state.machineHealthStatusFastLookup[componentKey]} onRow={(machine: MachineResource) => this.renderMachine(machine, machineIdsTobeUpgraded.includes(machine.Id))} onRowRedirectUrl={(machine: MachineResource) => {
                if (machineIdsTobeUpgraded.includes(machine.Id)) {
                    return isWorkerMachine(machine)
                        ? links.workerConnectivityPage.generateUrl({ spaceId: machine.SpaceId, machineId: machine.Id })
                        : links.deploymentTargetConnectivityPage.generateUrl({ spaceId: machine.SpaceId, machineId: machine.Id });
                }
                return isWorkerMachine(machine) ? links.workerSettingsPage.generateUrl({ spaceId: machine.SpaceId, machineId: machine.Id }) : links.deploymentTargetSettingsPage.generateUrl({ spaceId: machine.SpaceId, machineId: machine.Id });
            }} onLoadMore={async () => {
                const newTakeSize = this.getBaseState((state) => state.machineHealthStatusFastLookup[componentKey].ItemsPerPage + this.machineListTakeSize);
                const machineHealthStatusFastLookup = this.getBaseState((state) => state.machineHealthStatusFastLookup);
                const filteredMachines = allMachines.filter((x) => x.HealthStatus === status);
                const machinesForHealthStatus = toPaginatedCollection(filteredMachines, newTakeSize);
                machineHealthStatusFastLookup[componentKey] = machinesForHealthStatus;
                this.setState({
                    machineHealthStatusFastLookup,
                });
            }}/>
            </div>) : null;
    }
    private renderDisabledSectionTitle(machines: MachineResource[]) {
        const title = (<div className={styles.healthStatusCardTitleContainer} key={status}>
                <div className={styles.healthStatusIconContainer}>{this.props.isNewStatusIconsFeatureEnabled ? <StatusBadge status="disabled" size="small"/> : <DisabledMachineIcon title="Health Status"/>}</div>
                <div className={styles.healthStatusName}>Disabled</div>
                <div className={styles.healthStatusMachinesCount}>({machines.length})</div>
            </div>);
        return <FormSectionHeading title={title}/>;
    }
    private renderDisabledSectionHeading(allMachines: MachineResource[]): JSX.Element | null {
        const machines = allMachines.filter((x) => x.IsDisabled);
        const componentKey = "Disabled";
        return machines.length > 0 ? (<div>
                {this.renderDisabledSectionTitle(machines)}
                <MachinesList key={componentKey} initialData={this.state.machineHealthStatusFastLookup[componentKey]} onRow={(machine: MachineResource) => this.renderMachine(machine)} onRowRedirectUrl={(machine: MachineResource) => isWorkerMachine(machine) ? links.workerSettingsPage.generateUrl({ spaceId: machine.SpaceId, machineId: machine.Id }) : links.deploymentTargetSettingsPage.generateUrl({ spaceId: machine.SpaceId, machineId: machine.Id })} onLoadMore={async () => {
                const newTakeSize = this.getBaseState((state) => state.machineHealthStatusFastLookup[componentKey].ItemsPerPage + this.machineListTakeSize);
                const machineHealthStatusFastLookup = this.getBaseState((state) => state.machineHealthStatusFastLookup);
                const filteredMachines = allMachines.filter((x) => x.IsDisabled);
                const machinesForHealthStatus = toPaginatedCollection(filteredMachines, newTakeSize);
                machineHealthStatusFastLookup[componentKey] = machinesForHealthStatus;
                this.setState({
                    machineHealthStatusFastLookup,
                });
            }}/>
            </div>) : null;
    }
    private getBaseState = <TResult extends {}>(accessor: (state: Readonly<BaseMachinesSummaryState>) => TResult) => {
        return accessor(this.state as BaseMachinesSummaryState);
    };
    static displayName = "BaseMachinesSummary";
}
function isEnvironmentSummary(summary: EnvironmentSummaryV2Resource | WorkerPoolSummaryResource): summary is EnvironmentSummaryV2Resource {
    return "Environment" in summary;
}
type HelpLinkType = {
    label: string;
    path: string;
};
interface EmptyStateNoMachineProps {
    title?: string;
    description?: string;
    helpLink?: HelpLinkType;
}
export function EmptyStateNoMachine({ title, description, helpLink }: EmptyStateNoMachineProps) {
    return (<div className={emptyStateNoMachineStyles.root}>
            {title && <h3 className={emptyStateNoMachineStyles.title}>{title}</h3>}
            {description && <p className={emptyStateNoMachineStyles.description}>{description}</p>}
            {helpLink && <ExternalLink href={helpLink.path}>{helpLink.label}</ExternalLink>}
        </div>);
}
const emptyStateNoMachineStyles = {
    root: css({
        backgroundColor: themeTokens.color.background.secondary.default,
        borderRadius: borderRadius.large,
        padding: space["32"],
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: space[16],
    }),
    title: css({
        font: text.body.bold.medium,
        margin: 0,
    }),
    description: css({
        font: text.body.regular.medium,
        color: themeTokens.color.text.secondary,
        margin: 0,
    }),
};
export default BaseMachinesSummary;
